import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import BrandMark from '../components/BrandMark';

export default function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const params = new URLSearchParams(location.search);
  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        await signUp(email, password, fullName);
      }
      const { data: sessionRes } = await supabase.auth.getUser();
      const uid = sessionRes.user?.id;
      let target = '/learn';
      if (uid) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', uid)
          .maybeSingle();
        if (profile && !profile.onboarding_completed) {
          target = '/onboarding';
        }
      }
      navigate(target);
    } catch {
      // toast handled in context
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-warm-50 px-8 py-10 flex flex-col">
        <Link to="/" aria-label="PromptGPT home">
          <BrandMark tone="dark" size={26} />
        </Link>
        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-sm">
            <h1 className="font-display text-4xl mb-2">{mode === 'signin' ? 'Welcome back.' : 'Create your account.'}</h1>
            <p className="text-ink-600 mb-8">{mode === 'signin' ? 'Continue your AI learning journey.' : 'Start learning AI by doing real work with it.'}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className="field-label block mb-1.5">Full name</label>
                  <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                </div>
              )}
              <div>
                <label className="field-label block mb-1.5">Email</label>
                <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <label className="field-label block mb-1.5">Password</label>
                <input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
                {submitting ? 'Please wait...' : mode === 'signin' ? 'Sign in' : 'Create account'} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <div className="mt-6 text-sm text-ink-500">
              {mode === 'signin' ? (
                <>New here? <button onClick={() => setMode('signup')} className="text-accent-700 font-medium hover:underline">Create an account</button></>
              ) : (
                <>Already have an account? <button onClick={() => setMode('signin')} className="text-accent-700 font-medium hover:underline">Sign in</button></>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block bg-ink-900 text-warm-50 p-12 relative overflow-hidden">
        <div className="relative z-10 h-full flex flex-col justify-center max-w-md">
          <div className="text-xs uppercase tracking-[0.25em] text-accent-300 mb-4">Premier AI learning platform</div>
          <h2 className="font-display text-5xl leading-tight mb-6">Hook. Try. Reflect. Apply.</h2>
          <p className="text-ink-200 text-lg leading-relaxed mb-8">
            Every lesson follows the same shape, so the focus stays on the thinking, not the interface. Your reflections build a notebook you actually return to.
          </p>
          <div className="space-y-3">
            {['Guided paths for every learner', 'Four-beat lessons that build real fluency', 'A reflection notebook you own', 'Family-safe, with full guardian visibility'].map((f) => (
              <div key={f} className="flex items-center gap-3 text-ink-200">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-400" /> {f}
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-accent-500/20 blur-3xl" />
      </div>
    </div>
  );
}
